/**
 * ════════════════════════════════════════════════════════════════
 *   📝 CONTENU DU SITE — La Table d'Albigny
 * ════════════════════════════════════════════════════════════════
 *
 *  Ce fichier contient TOUS les textes affichés sur le site.
 *  Pour modifier un texte, changez simplement la valeur entre les
 *  guillemets, sauvegardez le fichier, le site se met à jour seul.
 *
 *  Règles simples :
 *   • Gardez les guillemets " " ou ' ' autour de chaque texte
 *   • Si vous tapez une apostrophe dans un texte entre " ", écrivez : d'Albigny
 *     (pas de problème). Si vous utilisez des ' ', échappez avec \' : d\'Albigny
 *   • Pour mettre un texte en italique dans un titre, utilisez la
 *     propriété "italic" prévue (ex: title + titleItalic)
 *   • Ne supprimez pas les virgules en fin de ligne
 *
 * ════════════════════════════════════════════════════════════════
 */

const content = {
    // ─── INFOS GÉNÉRALES ──────────────────────────────────────────
    brand: {
        name: "La Table d'Albigny",
        tagline: "Restaurant bistronomique",
        location: "Albigny-sur-Saône",
    },

    contact: {
        addressLine1: "11 Place de Verdun",
        addressLine2: "69250 Albigny-sur-Saône",
        phone: "09 84 01 63 97",
        phoneIntl: "+33984016397", // pour le lien d'appel - ne pas mettre d'espaces
        email: "t.touvier@latabledalbigny.fr",
        hoursLine1: "Du mardi au samedi midi",
        hoursLine2: "Vendredi & samedi soir",
        reservationUrl: "https://dujour.pro/iframe/booking/5k6k",
        menuPdfUrl: "https://customer-assets-wrfwihn1.emergentagent.net/job_showcase-redesign-6/artifacts/2ea92b1024deb163_CARTE%20AUTOMNE%202026.pdf",
        mapsUrl: "https://www.google.com/maps?q=11+Place+de+Verdun,+69250+Albigny-sur-Sa%C3%B4ne",
    },

    // ─── BARRE DE NAVIGATION ──────────────────────────────────────
    nav: {
        links: [
            { href: "/#histoire", label: "L'Histoire" },
            { href: "/galerie", label: "Galerie" },
            { href: "/bon-cadeau", label: "Bon Cadeau" },
        ],
        reserveBtn: "Réserver",
        reserveBtnMobile: "Réserver une table",
    },

    // ─── HERO (image plein écran d'accueil) ───────────────────────
    hero: {
        overline: "Restaurant bistronomique · Albigny-sur-Saône",
        title: "Cuisine de saison",
        titleItalic: "et du marché", // 2ème ligne en italique
        subtitle:
            "Une cuisine de passionnés, inspirée de produits de saison",
        primaryCta: "Réserver une table",
        secondaryCta: "Découvrir la carte",
        scrollLabel: "Découvrir",
    },

    // ─── SECTION HISTOIRE ─────────────────────────────────────────
    story: {
        overline: "— L'Histoire",
        title: "Une cuisine de",
        titleItalic: "passionnés",
        titleEnd: ", à quatre mains.",
        paragraphs: [
            "Venez déguster nos plats gourmands et raffinés, dans notre salle chaleureuse et cosy au cœur d'Albigny-sur-Saône.",
            "Tous les deux passionnés de gastronomie, nous travaillons à quatre mains afin de vous proposer une cuisine qui nous ressemble — dans un esprit très convivial.",
        ],
        stats: [
            { value: "100%", label: "Producteurs locaux" },
            { value: "04", label: "Mains expertes" },
            { value: "Saison", label: "Carte évolutive" },
        ],
    },

    // ─── SECTION NOTRE CARTE ──────────────────────────────────────
    menu: {
        overline: "— Notre Carte",
        title: "Au gré des saisons,",
        titleItalic: "au rythme du marché.",
        subtitle:
            "Notre carte évolue avec les arrivages. Téléchargez-la pour découvrir nos plats du moment et nos formules.",
        formulas: [
            {
                icon: "Utensils", // icônes disponibles : Utensils, ChefHat, Clock, Wine, Salad, Coffee
                when: "Midi · du mardi au samedi",
                title: "Menu du jour",
                desc: "Une formule courte, fraîche, composée chaque matin avec les arrivages du marché.",
            },
            {
                icon: "ChefHat",
                when: "Midi & soir",
                title: "Carte de saison",
                desc: "Plats gourmands et raffinés, élaborés avec des produits fermiers sélectionnés chez nos producteurs locaux.",
            },
            {
                icon: "Clock",
                when: "Vendredi & samedi soir",
                title: "Soirs bistronomiques",
                desc: "Dans une salle chaleureuse et cosy, une expérience pensée pour les amoureux de la table.",
            },
        ],
        downloadBtn: "Télécharger la carte",
        reserveBtn: "Réserver une table",
    },

    // ─── SECTION LE CHEF ──────────────────────────────────────────
    chef: {
        overline: "— Le Chef",
        title: "Thierry Touvier,",
        titleItalic: "l'amour du produit.",
        imageAlt: "Thierry Touvier, chef du restaurant",
        paragraphs: [
            "La cuisine de Thierry s'écrit à partir d'un seul principe : le respect du produit. Chaque matin, sa journée commence chez les producteurs voisins, à la rencontre des maraîchers, éleveurs et artisans de la région lyonnaise.",
            "De cette quête naissent des plats sincères, où la technique sert toujours le goût — une cuisine instinctive, généreuse, qui célèbre les saisons et le savoir-faire français.",
        ],
        quote: "Une cuisine qui nous ressemble, dans un esprit très convivial.",
        quoteAuthor: "— Thierry & son équipe",
    },

    // ─── SECTION CONTACT ──────────────────────────────────────────
    contactSection: {
        overline: "— Contact & Réservation",
        title: "Réservez votre",
        titleItalic: "table.",
        subtitle:
            "Une demande, une remarque ou simplement envie de prendre contact ? Nous serons heureux de vous répondre.",
        form: {
            firstName: "Prénom",
            lastName: "Nom",
            email: "E-mail",
            phone: "Téléphone",
            message: "Votre message",
            submit: "Envoyer le message",
            submitting: "Envoi en cours…",
            successToast:
                "Votre message a bien été envoyé. Nous vous répondrons rapidement.",
            errorToast: "Une erreur est survenue. Merci de réessayer.",
            requiredToast: "Merci de renseigner les champs obligatoires.",
        },
        info: {
            heading: "Où nous trouver",
            addressLabel: "Adresse",
            hoursLabel: "Heures d'ouverture",
            phoneLabel: "Téléphone",
            emailLabel: "E-mail",
            reserveBtn: "Réserver en ligne",
        },
    },

    // ─── SECTION GALERIE ──────────────────────────────────────────
    gallery: {
        overline: "— Galerie",
        title: "Un aperçu...",
        titleItalic: "",
        subtitle: "",
    },

    // ─── FOOTER ───────────────────────────────────────────────────
    footer: {
        tagline: "Restaurant bistronomique",
        description:
            "Une cuisine de saison et du marché, à quatre mains. Produits du marché",
        sectionVisitTitle: "Visiter",
        sectionContactTitle: "Coordonnées",
        copyright: "Tous droits réservés.",
        quote: "« Une cuisine qui nous ressemble. »",
    },
};

export default content;
